import { useEffect, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';

/**
 * The working copy's change signal (apiserver previewStamp.js): a short stamp
 * that moves when an agent's edit lands, and whether the agent is calling
 * tools right now. Cheap to ask every few seconds; the heavier data is
 * refetched only when the stamp moves.
 */
const CHANGE_STAMP = gql`
  query TinycmsChangeStamp($accountId: ID!) {
    tinycmsChangeStamp(accountId: $accountId)
  }
`;

const EVERY_MS = 3000;
const HOLD_MS = 3000;

/**
 * While an approval view is open: follow the agent's edits. On a change it
 * calls onChange (refetch what the view shows) and reports justChanged for a
 * moment, so Approve waits while the preview catches up — the person approves
 * the version they can see, never one that landed a second ago.
 */
export function useLiveChanges(active: boolean, onChange: () => unknown) {
  const { accountId } = useAccount();
  const { data } = useQuery(CHANGE_STAMP, {
    variables: { accountId },
    skip: !active || !accountId,
    fetchPolicy: 'network-only',
    pollInterval: active ? EVERY_MS : 0,
    notifyOnNetworkStatusChange: false,
  });
  const stamp: string | undefined = data?.tinycmsChangeStamp?.stamp;
  const agentWorking = Boolean(active && data?.tinycmsChangeStamp?.agentWorking);
  const last = useRef<string | undefined>(undefined);
  const latest = useRef(onChange);
  latest.current = onChange;
  const [justChanged, setJustChanged] = useState(false);

  useEffect(() => {
    if (!stamp) return;
    const before = last.current;
    last.current = stamp;
    if (!before || before === stamp) return;
    setJustChanged(true);
    void latest.current();
    const t = setTimeout(() => setJustChanged(false), HOLD_MS);
    return () => clearTimeout(t);
  }, [stamp]);

  return { agentWorking, justChanged };
}
